import type {
  ISearchSuggestions,
  ISearchTermsSuggestion
} from '@/services/models/SearchSuggestions';
import SearchSuggestionsModel from '@/services/models/SearchSuggestions';
import type { DTO, IDisposable, MaybePromise, Nullable } from '@/type-utils';
import {
  action,
  computed,
  makeAutoObservable,
  reaction,
  runInAction,
  type IReactionDisposer
} from 'mobx';
import SearchSuggestionsService from '@/services/isomorphic/SearchSuggestionsService';
import { Debouncer } from '@/utils/Debouncer';
import type { GetSuggestionsBySearchTermsOptions } from '@/services/isomorphic/SearchSuggestionsService/IGetSuggestionsBySearchTermsParams';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import ConfigurationService, {
  type Config
} from '@/services/isomorphic/ConfigurationService';
import { ProductModel, type IProduct } from '@/services/models/Product';
import WebStorageService from '@/services/client/WebStorageService';
import { isArrayOf, unique } from '@/utils/array-utils';
import type { ISearchMetadata } from '@/services/models/SearchSuggestions/ISearchMetadata';
import UserInteractionService, {
  EventType
} from '@/services/isomorphic/UserInteractionService';
import type { IInteractionProductDetailsWithSearch } from '@/services/isomorphic/UserInteractionService/IInteractionDetails';

/**
 * It also handles the debouncing of search term input to avoid excessive API calls.
 */
export class SearchBoxVM implements IDisposable {
  private _term: string = '';
  private _lastSearchTerm: string = '';
  private _isLoading: boolean = false;
  private _debouncer: Debouncer;
  private _productSuggestions: Nullable<
    MaybePromise<ReadonlyArray<ProductModel>>
  >;

  private _localStorageSearchHistory: Array<string>;
  private _localStorageDisposal: IReactionDisposer;

  /**
   * This metadata is returned by the initial model initialization that likely
   * comes from the server, and by search interactions.
   */
  private _searchMetadata: MaybePromise<Nullable<ISearchMetadata>>;

  private _searchTermsSuggestions: Nullable<
    MaybePromise<ReadonlyArray<ISearchTermsSuggestion>>
  >;

  private _didYouMean: Nullable<ReadonlyArray<ISearchTermsSuggestion>>;

  private _productSuggestionsByTerm: Nullable<ReadonlyArray<ProductModel>>;

  /**
   * @returns - The current search term.
   */
  public get term(): string {
    return this._term;
  }

  /**
   * @returns - The last search term used in a successful search.
   */
  public get lastSearchTerm(): string {
    return this._lastSearchTerm;
  }

  /**
   * A boolean indicating whether the search is currently loading.
   * @returns - The loading status of the search.
   */
  public get isLoading(): boolean {
    return this._isLoading;
  }

  /**
   * The product suggestions returned by the search.
   *
   * This should be populated on initialization, to suggest products based on an empty search.
   *
   * @returns - The product suggestions returned by an empty search.
   */
  public get productSuggestions(): MaybePromise<ReadonlyArray<ProductModel>> {
    return this._productSuggestions ?? [];
  }

  /**
   * The product suggestions returned upon a search term.
   *
   * This should be populated when the user types something in the search box.
   *
   * @returns - The product suggestions returned by the search.
   */
  public get productSuggestionsByTerm(): ReadonlyArray<ProductModel> {
    return this._productSuggestionsByTerm ?? [];
  }

  /**
   * The search terms suggestions returned by the search.
   *
   * The search terms suggestions are populated when the view model is initialized. So upon empty search,
   * they are the suggestions that are shown to the user. We fallback to this if the user
   * doesn't type anything in the search box of if the search fails to produce results in
   * the `didYouMean` property.
   *
   * @returns - The search terms suggestions returned by the default `''` search.
   */
  public get searchTermsSuggestions(): MaybePromise<
    ReadonlyArray<ISearchTermsSuggestion>
  > {
    return this._searchTermsSuggestions ?? [];
  }

  /**
   * Same structure as the `searchTermsSuggestions` but with a different purpose. This one is
   * used to show the user suggestions based on the search term he typed.
   *
   * We fill this property when the user types something in the search box and clear it if
   * the search term is empty.
   *
   * @returns - The search terms suggestions returned by the  `this.term` search.
   */
  public get didYouMean(): ReadonlyArray<ISearchTermsSuggestion> {
    return this._didYouMean ?? [];
  }

  /**
   * Base configuration for the search.
   *
   * @returns The configuration to be used on the search.
   */
  public get config(): Config<'search'> {
    return ConfigurationService.getConfig('search');
  }

  /**
   * A setter for the search term.
   *
   * This setter updates the term and triggers a debounced function to fetch
   * search suggestions. The debounced function will only execute after a
   * specified delay.
   *
   * @param value - The new search term.
   * @param extra - Extra props to search for.
   * @throws Will throw an error if the search fails.
   */
  @action
  public searchFor(
    value: string,
    extra: GetSuggestionsBySearchTermsOptions
  ): void {
    // If the term hasn't changed, we don't need to search again.
    if (this._term === value) {
      return;
    }
    this._term = value;
    this._debouncer.execute<Promise<void>>(async () => {
      try {
        runInAction(() => {
          this._isLoading = true;
        });

        const suggestionsDTO =
          await SearchSuggestionsService.getSuggestionsBySearchTerms(
            { term: this._term, ...extra },
            this._debouncer.abortControllerSignal
          );
        const suggestionsModel = SearchSuggestionsModel.from(suggestionsDTO);

        runInAction(() => {
          // Now that the search is done, we can update the last search term.
          this._lastSearchTerm = value;
        });
        this.updateResults(suggestionsModel);
      } catch (error) {
        // If it's an AbortError, we can ignore it
        // since it means the user has changed the search term before the request completed.
        if (error instanceof Error && error.name === 'AbortedRequestError')
          return;

        runInAction(() => {
          this._isLoading = false;
          this._productSuggestions = null;
          this._didYouMean = null;
          this._searchTermsSuggestions = null;
          this._searchMetadata = null;
        });
        // If it's not an AbortError, we can throw it to be handled by the caller.
        throw error;
      }
    });
  }

  /**
   * Retrieves a sanitized version of the search history from local storage.
   *
   * This will return an array of unique search terms, ensuring that
   * the maximum number of searches defined in the configuration is respected.
   *
   * The search history is stored in the local storage under a key defined in the configuration.
   *
   * @returns - An array of unique search terms from the search history.
   */
  @computed
  public get searchHistory(): Array<string> {
    const maxSearches = this.config.getSetting(
      'searchHistory.numberOfSearches'
    ).value;

    const sanitizedHistory =
      // It should be a non-nullish array of strings.
      !isNullish(this._localStorageSearchHistory) &&
      isArrayOf<string>(
        this._localStorageSearchHistory,
        (val: unknown) => typeof val === 'string'
      )
        ? // If it's a valid array of strings, we return it as a unique array of values
          // We do this to avoid having duplicate search terms in the history shown to the user.
          unique(this._localStorageSearchHistory)
        : // If it's not a valid array of strings, we initialize it as an empty array.
          [];

    return sanitizedHistory.length > maxSearches
      ? // If the history is longer than the maximum number of searches,
        // we slice it to the maximum number of searches allowed.
        sanitizedHistory.slice(0, maxSearches)
      : sanitizedHistory;
  }

  /**
   * Saves the current search term or a provided term to the search history.
   *
   * This will use the local storage to save the search term,
   * ensuring that it doesn't exceed the maximum number of searches allowed
   * defined in the configuration.
   *
   * If the term is already in the history, it won't be added again.
   *
   * @param term - Optional search term to save. If not provided, the current term will be used.
   */
  public saveTermToHistory(term?: string): void {
    // If the term is not provided, we use the current term.
    const termToSave = (term ?? this._term).trim();
    const maxSearches = this.config.getSetting(
      'searchHistory.numberOfSearches'
    ).value;

    // We only want to save the term if it's not null or empty.
    if (!isNullOrEmpty(termToSave)) {
      // If the term is already in the history, we don't want to add it again.
      // Instead, we want to position it at the top of the history.
      if (this.searchHistory.includes(termToSave)) {
        const historyToSave = this.searchHistory.filter(
          (term) => term !== termToSave
        );
        historyToSave.unshift(termToSave);

        this._localStorageSearchHistory = historyToSave;
        return;
      }

      // If the history is already at the maximum number of searches,
      // we need to remove the oldest search term before adding the new one.
      if (this.searchHistory.length >= maxSearches) {
        const historyToSave = this.searchHistory.slice(0, maxSearches - 1);
        historyToSave.unshift(termToSave);

        this._localStorageSearchHistory = historyToSave;
        return;
      }

      // If the history is not at the maximum number of searches,
      // we can simply add the new term to the top of the history.
      const historyToSave = this.searchHistory;
      historyToSave.unshift(termToSave);

      this._localStorageSearchHistory = historyToSave;
    }
  }

  /**
   * Deletes a specific term from the search history.
   *
   * @param term - The search term to delete from the history.
   */
  @action
  public deleteTermFromHistory(term: string): void {
    const termToDelete = term.trim();

    // Filter out from the search history the term to delete.
    // This is safe because the history is already sanitized and `unique`
    // so we are sure that only one term will be deleted if it exists.
    const historyToSave = this.searchHistory.filter(
      (searchTerm) => searchTerm !== termToDelete
    );
    this._localStorageSearchHistory = historyToSave;
  }

  /**
   * Small helper function to convert the suggestions DTOs into ProductModel instances.
   *
   * The {@link ProductModel} is a little bit more useful than the DTO for our use case,
   * since it contains some additional methods and properties that are useful in the UI,
   * like the categories urls.
   *
   * @param suggestions - The DTO<IProduct> suggestions to convert.
   * @returns - An array of ProductModel instances.
   */
  private productsModelsFromSuggestions(
    suggestions: ReadonlyArray<DTO<IProduct>>
  ): ReadonlyArray<ProductModel> {
    return suggestions.map((product) => ProductModel.from(product));
  }

  /**
   * Mutates the view model state with the results of the search suggestions.
   *
   * According to the current search term, it updates the product suggestions,
   * search terms suggestions, and the did you mean suggestions.
   *
   * @param suggestionsModel - The model containing the search suggestions.
   */
  @action
  private updateResults(suggestionsModel: SearchSuggestionsModel): void {
    if (this._term.length === 0) {
      // If the term is empty, we want to update and show the search terms suggestions
      this._searchTermsSuggestions = suggestionsModel.searchTermsSuggestions;
      this._productSuggestions = this.productsModelsFromSuggestions(
        suggestionsModel.productSuggestions
      );
      // The `didYouMean` is not shown when the term is empty
      // since it doesn't make sense to show suggestions for a term that doesn't exist
      this._didYouMean = [];
      this._productSuggestionsByTerm = [];
    }

    if (this._term.length > 0) {
      // If the term is not empty, we start suggesting terms and products based on the term the user
      // typed in the search box.
      this._didYouMean = suggestionsModel.searchTermsSuggestions;
      this._productSuggestionsByTerm = this.productsModelsFromSuggestions(
        suggestionsModel.productSuggestions
      );
    }

    this._searchMetadata = suggestionsModel.metadata;
    this._isLoading = false;
  }

  /**
   * This helper function encodes the search term and returns the URL to navigate to.
   *
   * This can be used to navigate to a search results page with the current search term or a different one.
   *
   * @param term - An optional parameter to search for something else. If not provided, the current term will be used.
   * @returns The URL to navigate to.
   */
  public encodeSearchResultsPageNavigationUrl(term?: string): string {
    const path = this.config.getSetting('searchResults.path').value;
    const q = (term ?? this._term).trim(); // Trim the term to avoid leading/trailing spaces.
    // Use querystring to encode the search term.
    const encodedParams = new URLSearchParams({ q }).toString();
    // Use the router to push the new URL with the search term.
    return `${path}${isNullOrEmpty(encodedParams) ? '' : `?${encodedParams}`}`;
  }

  /**
   * Based on a given product suggestion and it's position in the list,
   * this function dispatches an analytics event to track the product suggestion click.
   *
   * @param product - The product that was clicked on the tile.
   * @param position - The position of the product in the grid.
   */
  public async dispatchProductClickAnalyticsEvent(
    product: IProduct,
    position: number
  ): Promise<void> {
    const searchMetadata = await this._searchMetadata;
    if (!isNullish(searchMetadata)) {
      const interaction: IInteractionProductDetailsWithSearch = {
        action: EventType.SearchTileClick,
        product,
        searchData: { ...searchMetadata, documentPosition: position }
      };

      UserInteractionService.fireAndForgetAction(interaction);
    }
  }

  /** @inheritdoc */
  public constructor(
    private debounceTime: number,
    initialSuggestionsPromise?: Promise<ISearchSuggestions | undefined>
  ) {
    initialSuggestionsPromise = Promise.resolve(initialSuggestionsPromise); // convert from ReactPromise to regular Promise and narrow type

    this._debouncer = Debouncer.milliseconds(this.debounceTime);
    this._productSuggestions = initialSuggestionsPromise.then((suggestions) =>
      this.productsModelsFromSuggestions(suggestions?.productSuggestions ?? [])
    );
    this._searchTermsSuggestions = initialSuggestionsPromise.then(
      (suggestions) => suggestions?.searchTermsSuggestions ?? []
    );
    this._searchMetadata = initialSuggestionsPromise.then(
      (suggestions) => suggestions?.metadata
    );

    const localStorageKey = this.config.getSetting('searchHistory.key').value;

    this._localStorageSearchHistory =
      WebStorageService.localStorage?.getItem<Array<string>>(localStorageKey) ??
      [];

    makeAutoObservable(this);

    // We want to save the search history to the local storage whenever it changes.
    //
    // This reaction will make all of the changes to the `_localStorageSearchHistory`
    // observable, so that the local storage is updated whenever the search history changes.
    //
    // It will also provide us a `reactive` way to display the search history
    // in the UI, since the `searchHistory` computed property will be updated
    // whenever the `_localStorageSearchHistory` changes.
    //
    // That will return a `IReactionDisposer` from mobx that we will use to dispose of the reaction
    // when the view model is disposed.
    this._localStorageDisposal = reaction(
      () => this._localStorageSearchHistory,
      (value) => WebStorageService.localStorage?.setItem(localStorageKey, value)
    );
  }

  /** @inheritdoc */
  @action
  public dispose(): void {
    // When disposing, we need to cancel the debouncer to avoid memory leaks.
    // This is important to ensure that the abort controller is cleaned up.
    this._debouncer.cancel();
    // We also need to dispose of the local storage reaction.
    this._localStorageDisposal();
  }
}
